import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useBulkUpdateJob } from '@/hooks/agent/useBulkUpdate';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useEffect, useState } from 'react';

interface BulkUpdateProgressDialogProps {
  isOpen: boolean;
  jobId: number | null;
  onClose: () => void;
}

export const BulkUpdateProgressDialog = ({
  isOpen,
  jobId,
  onClose,
}: BulkUpdateProgressDialogProps) => {
  const { data: jobStatus } = useBulkUpdateJob(jobId, isOpen);
  const [shouldClose, setShouldClose] = useState(false);

  useEffect(() => {
    if (jobStatus?.job.status === 'completed' || jobStatus?.job.status === 'failed') {
      // Auto-close after 2 seconds if completed/failed
      const timer = setTimeout(() => {
        setShouldClose(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [jobStatus?.job.status]);

  useEffect(() => {
    if (shouldClose) {
      onClose();
      setShouldClose(false);
    }
  }, [shouldClose, onClose]);

  if (!jobStatus) return null;

  const progress = jobStatus.progress.total > 0
    ? ((jobStatus.progress.completed + jobStatus.progress.failed) / jobStatus.progress.total) * 100
    : 0;

  const isComplete = jobStatus.job.status === 'completed' || jobStatus.job.status === 'failed';

  return (
    <Dialog open={isOpen && !shouldClose} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Bulk Update Progress</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 min-h-0 flex flex-col">
          {/* Progress Bar */}
          <div className="space-y-2 flex-shrink-0">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Overall Progress</span>
              <span className="font-medium">
                {jobStatus.progress.completed + jobStatus.progress.failed}/{jobStatus.progress.total}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Status Summary */}
          <div className="grid grid-cols-3 gap-4 flex-shrink-0">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{jobStatus.progress.completed}</div>
              <div className="text-xs text-gray-600 mt-1">Completed</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-amber-600">{jobStatus.progress.pending}</div>
              <div className="text-xs text-gray-600 mt-1">Pending</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{jobStatus.progress.failed}</div>
              <div className="text-xs text-gray-600 mt-1">Failed</div>
            </div>
          </div>

          {/* Item Status */}
          <div className="max-h-60 overflow-y-auto space-y-2 min-h-0">
            {jobStatus.items.map((item) => (
              <div key={item.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                {item.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                {item.status === 'failed' && <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                {item.status === 'pending' && <Loader2 className="h-4 w-4 text-blue-500 flex-shrink-0 animate-spin" />}

                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 truncate">{item.agent_id}</p>
                  {item.error && <p className="text-xs text-red-600 truncate">{item.error}</p>}
                </div>
              </div>
            ))}
          </div>

          {/* Status Message */}
          {isComplete && (
            <div className={`p-3 rounded text-sm font-medium flex-shrink-0 ${
              jobStatus.job.status === 'completed'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}>
              {jobStatus.job.status === 'completed'
                ? '✓ Bulk update completed successfully'
                : '✗ Bulk update completed with errors'}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
